import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// @@QUICKQUOTE_INTERNAL_v75@@ Internal quick-quote page. Same server-priced
// endpoints pattern as the client portal (shared lib server-side) — only
// customer-facing numbers ever reach the browser, so all internal roles may
// use it. Skinny single-column layout per Rob's reference.
const GREEN = '#31b44b';

export default function QuickQuote() {
  const [catalog, setCatalog] = useState(null); // null=loading, {available,...}
  const [category, setCategory] = useState('');
  const [garment, setGarment] = useState('');
  const [orderType, setOrderType] = useState('new');
  const [printLoc, setPrintLoc] = useState('front');
  const [qty, setQty] = useState('');
  const [frontColors, setFrontColors] = useState('');
  const [backColors, setBackColors] = useState('');
  const [discharge, setDischarge] = useState(false);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiFetch('/api/quote/catalog')
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        setCatalog(d);
        if (d && d.available) {
          for (const c of d.categories) {
            const def = c.garments.find((g) => g.is_default);
            if (def) { setCategory(c.name); setGarment(def.name); break; }
          }
        }
      })
      .catch(() => setCatalog({ available: false }));
  }, []);

  const cats = (catalog && catalog.available && catalog.categories) || [];
  const curCat = cats.find((c) => c.name === category);
  const garments = curCat ? curCat.garments : [];
  const g = garments.find((x) => x.name === garment) || null;
  const dischargeOk = !!(g && g.discharge_available);

  function clearQuote() { setQuote(null); setError(''); }
  function pickCategory(name) { setCategory(name); setGarment(''); setDischarge(false); clearQuote(); }
  function pickGarment(name) {
    setGarment(name); clearQuote();
    const row = garments.find((x) => x.name === name);
    if (!row || !row.discharge_available) setDischarge(false);
  }
  function pickLoc(loc) { setPrintLoc(loc); clearQuote(); if (loc === 'front') setBackColors(''); }
  function resetForm() {
    setGarment(''); setOrderType('new'); setPrintLoc('front');
    setQty(''); setFrontColors(''); setBackColors(''); setDischarge(false);
    clearQuote();
  }

  async function getQuote() {
    setError(''); setBusy(true); setQuote(null);
    try {
      const res = await apiFetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          garment,
          order_type: orderType,
          print_config: printLoc,
          qty: qty === '' ? null : Number(qty),
          front_colors: frontColors === '' ? 0 : Number(frontColors),
          back_colors: printLoc === 'front' ? 0 : (backColors === '' ? 0 : Number(backColors)),
          discharge,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || 'Could not get a quote. Try again.'); return; }
      setQuote(d);
    } catch (e) {
      setError('Could not reach the server. Try again.');
    } finally { setBusy(false); }
  }

  function onEnter(e) { if (e.key === 'Enter') getQuote(); }
  const fmt = (n) => '$' + Number(n).toFixed(2);

  const sec = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hq-muted)', fontWeight: 700, margin: '24px 0 2px' };
  const lbl = { display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 6px' };
  const wide = { width: '100%', height: 42 };
  const seg = (active) => ({
    flex: 1, height: 40, borderRadius: 9, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
    border: '1px solid ' + (active ? GREEN : 'var(--hq-line)'),
    background: active ? GREEN : 'transparent',
    color: active ? '#fff' : 'var(--hq-muted)',
  });
  const bannerBox = (bg, border, color) => ({
    marginTop: 14, padding: '12px 14px', borderRadius: 10, fontSize: 13,
    lineHeight: 1.5, background: bg, border: '1px solid ' + border, color,
  });
  const row = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--hq-line)', fontSize: 14 };
  const divider = { borderTop: '1px solid var(--hq-line)', margin: '22px 0 0' };

  const shell = (inner) => (
    <div className="hq-page-content">
      <div style={{ maxWidth: 480, margin: '0 auto' }}>{inner}</div>
    </div>
  );

  if (catalog === null) {
    return shell(<div className="hq-panel"><div className="hq-empty">Loading garment catalog…</div></div>);
  }
  if (!catalog.available) {
    return shell(<div className="hq-panel"><div className="hq-empty">The garment catalog is temporarily unavailable. Please try again in a few minutes.</div></div>);
  }

  return shell(
    <>
      <div className="hq-panel" style={{ padding: '28px 28px 26px' }}>
        <h1 style={{ fontFamily: "'Archivo Expanded', 'Archivo', sans-serif", fontWeight: 700, fontSize: 22, margin: 0 }}>Get an Instant Quote</h1>
        <p style={{ color: 'var(--hq-muted)', fontSize: 14, margin: '6px 0 0' }}>Fill in your order details below to see your price breakdown.</p>

        <div style={sec}>Garment</div>
        <label style={lbl}>Category</label>
        <select className="hq-select" style={wide} value={category} onChange={(e) => pickCategory(e.target.value)}>
          <option value="">Select a category…</option>
          {cats.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <label style={lbl}>Garment</label>
        <select className="hq-select" style={wide} value={garment} disabled={!category} onChange={(e) => pickGarment(e.target.value)}>
          <option value="">{category ? 'Select a garment…' : 'Select a category first'}</option>
          {garments.map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}
        </select>
        {g && (g.weight || g.fabric || g.description) && (
          <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--hq-field)', border: '1px solid var(--hq-line)', borderRadius: 10, fontSize: 13, lineHeight: 1.6 }}>
            {g.weight && <div><strong>Weight:</strong> {g.weight} oz</div>}
            {g.fabric && <div><strong>Fabric:</strong> {g.fabric}</div>}
            {g.description && <div><strong>Garment Info:</strong> {g.description}</div>}
          </div>
        )}

        <div style={divider} />
        <div style={sec}>Order Details</div>
        <label style={lbl}>Order Type</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={seg(orderType === 'new')} onClick={() => { setOrderType('new'); clearQuote(); }}>New Order</button>
          <button type="button" style={seg(orderType === 'reorder')} onClick={() => { setOrderType('reorder'); clearQuote(); }}>Reorder</button>
        </div>
        <label style={lbl}>Print Configuration</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={seg(printLoc === 'front')} onClick={() => pickLoc('front')}>Front Print Only</button>
          <button type="button" style={seg(printLoc === 'front_back')} onClick={() => pickLoc('front_back')}>Front + Back Print</button>
        </div>

        <div style={sec}>Production Options</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, padding: '11px 14px', border: '1px solid var(--hq-line)', borderRadius: 10, fontSize: 14, color: dischargeOk ? 'var(--hq-txt)' : 'var(--hq-muted)', cursor: dischargeOk ? 'pointer' : 'default' }}>
          <input type="checkbox" disabled={!dischargeOk} checked={discharge}
            onChange={(e) => { setDischarge(e.target.checked); clearQuote(); }} />
          Print with Discharge Ink{!dischargeOk && g ? ' (not available for this garment)' : ''}
        </label>
        {discharge && (
          <div style={{ marginTop: 8, fontSize: 13, color: 'var(--hq-muted)' }}>
            <strong>Discharge printing does not require a white underbase. Enter only the visible ink colors.</strong>
          </div>
        )}

        <label style={lbl}>Quantity</label>
        <input className="hq-input" style={wide} type="number" min="72" placeholder="Enter quantity (72+)" value={qty}
          onChange={(e) => { setQty(e.target.value); clearQuote(); }} onKeyDown={onEnter} />
        <label style={lbl}>Front Colors</label>
        <input className="hq-input" style={wide} type="number" min="0" max="12" placeholder="0–12" value={frontColors}
          onChange={(e) => { setFrontColors(e.target.value); clearQuote(); }} onKeyDown={onEnter} />
        {printLoc === 'front_back' && (
          <>
            <label style={lbl}>Back Colors</label>
            <input className="hq-input" style={wide} type="number" min="0" max="12" placeholder="0–12" value={backColors}
              onChange={(e) => { setBackColors(e.target.value); clearQuote(); }} onKeyDown={onEnter} />
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button type="button" disabled={busy} onClick={getQuote}
            style={{ flex: 1, height: 44, borderRadius: 10, border: 'none', background: GREEN, color: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: busy ? .6 : 1 }}>
            {busy ? 'Calculating…' : 'Get Quote'}
          </button>
          <button type="button" onClick={resetForm}
            style={{ flex: 1, height: 44, borderRadius: 10, background: 'transparent', border: '1px solid ' + GREEN, color: GREEN, fontFamily: 'inherit', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            Reset
          </button>
        </div>

        {!quote && (
          <div style={bannerBox('var(--hq-field)', 'var(--hq-line)', 'var(--hq-muted)')}>
            Minimum order is <strong style={{ color: 'var(--hq-txt)' }}>72 pieces</strong>. Orders under 120 pieces are limited to <strong style={{ color: 'var(--hq-txt)' }}>4 colors per location</strong>. Pricing shown is an estimate to help you plan — it is not an official quote. Contact your XYZ rep for a formal quote.
          </div>
        )}
        {error && (
          <div role="alert" style={bannerBox('rgba(255,90,82,.08)', 'rgba(255,90,82,.35)', '#ff7a73')}>{error}</div>
        )}
      </div>

      {quote && (
        <div className="hq-panel" style={{ padding: '24px 28px', marginTop: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>Your Quote</h2>
          <div style={row}><span>Price Per Piece</span><strong>{fmt(quote.price_per_piece)}/pc</strong></div>
          <div style={row}><span>Production Subtotal</span><strong>{fmt(quote.production_subtotal)}</strong></div>
          <div style={row}>
            <span>{quote.setup.label}</span>
            <strong>{fmt(quote.setup_cost)} ({quote.setup.colors} × {fmt(quote.setup.fee_per_color)})</strong>
          </div>
          <div style={{ ...row, borderBottom: 'none', fontSize: 16 }}>
            <span style={{ fontWeight: 700 }}>Order Total</span>
            <strong style={{ fontFamily: "'Archivo Expanded', 'Archivo', sans-serif", color: GREEN }}>{fmt(quote.order_total)}</strong>
          </div>

          {quote.banner === 'small_run' && (
            <div style={bannerBox('rgba(232,193,90,.1)', 'rgba(232,193,90,.3)', '#e8c15a')}>
              This order qualifies for our small-run program. Orders between 72 and 119 pieces are limited to 4 colors per print location. For best pricing, we recommend ordering 120+ pieces.
            </div>
          )}
          {quote.banner === 'standard' && (
            <div style={bannerBox('rgba(80,220,140,.08)', 'rgba(80,220,140,.3)', '#5fdc97')}>
              Need artwork review, garment recommendations, or delivery information? Contact us to finalize your project.
            </div>
          )}
          {quote.banner === 'large_order' && (
            <div style={bannerBox('rgba(110,168,255,.08)', 'rgba(110,168,255,.3)', '#6ea8ff')}>
              <strong>Large Order Inquiry</strong><br />
              Orders over 1,200 pieces typically qualify for custom pricing. Contact us for a custom quote and production recommendations.
            </div>
          )}

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--hq-line)', fontSize: 12, color: 'var(--hq-muted)', lineHeight: 1.5 }}>
            This calculator helps you build an idea of pricing — the numbers above are an estimate and do not serve as an official quote. Please contact your XYZ Merchandise rep for a formal quote on your project.
          </div>
        </div>
      )}

      {quote && quote.next_break && (
        <div className="hq-panel" style={{ padding: '24px 28px', marginTop: 16, borderLeft: '3px solid ' + GREEN }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>Next Price Break</h2>
          <div style={row}>
            <span>Quantity</span>
            <strong>{quote.next_break.qty} pcs <span style={{ color: 'var(--hq-muted)', fontWeight: 400 }}>(+{quote.next_break.additional} additional pieces)</span></strong>
          </div>
          <div style={row}><span>Price Per Piece</span><strong>{fmt(quote.next_break.price_per_piece)}/pc</strong></div>
          <div style={{ ...row, borderBottom: 'none' }}><span>Order Total</span><strong>{fmt(quote.next_break.order_total)}</strong></div>
        </div>
      )}
    </>
  );
}
